import {
  Box,
  Button,
  makeStyles,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import axios from 'axios'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import useReservationClock, { getReservationStatus, hasDeparted } from './reservationLifecycle'
import ReservationHistory from './ReservationHistory'

const useStyles = makeStyles((theme) => ({
  dialog: {
    '&.modal-dialog': {
      width: 'calc(100% - 32px)',
      maxWidth: 900,
      margin: '1.75rem auto'
    }
  },
  details: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2, 3),
    margin: 0,
    '& dt': { marginBottom: theme.spacing(0.5) },
    '& dd': { margin: 0, overflowWrap: 'anywhere' },
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: 'minmax(0, 1fr)' }
  },
  fullWidth: { gridColumn: '1 / -1' },
  section: { marginTop: theme.spacing(3) },
  participants: { margin: 0, paddingLeft: theme.spacing(2.5) },
  participant: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  remove: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    '&.Mui-disabled': { opacity: 0.5 }
  },
  plate: { whiteSpace: 'nowrap' },
  reason: { whiteSpace: 'pre-wrap' }
}))

const personName = (person) =>
  person
    ? [person.first_name, person.last_name].filter(Boolean).join(' ') || person.email || 'Profili nuk është i disponueshëm'
    : 'Profili nuk është i disponueshëm'

const Detail = ({ label, children, className }) => (
  <div className={className}>
    <Typography component='dt' variant='body2' color='textSecondary'>
      {label}
    </Typography>
    <Typography component='dd' variant='body1'>
      {children}
    </Typography>
  </div>
)

const ReservationDetailsModal = ({ reservationId, management = false, onClose, onEdit, onChanged }) => {
  const classes = useStyles()
  const [reservation, setReservation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [reload, setReload] = useState(0)
  const [tab, setTab] = useState('details')
  const [removingId, setRemovingId] = useState(null)
  const [removeError, setRemoveError] = useState('')
  const removing = useRef(false)
  const now = useReservationClock(reservation ? [reservation] : [], () => setReload((previous) => previous + 1))

  useEffect(() => {
    setRemoveError('')
    setTab('details')
  }, [reservationId, management])

  const close = () => {
    if (!removing.current) onClose()
  }

  const removeParticipant = async (person) => {
    if (removing.current) return
    removing.current = true
    setRemovingId(person._id)
    setRemoveError('')
    try {
      const { data } = await axios.post(`vehicle-reservations/${reservationId}/participants/${person._id}/remove`)
      if (!data.success || !data.data) throw new Error('Participant removal unavailable')
      setReload((previous) => previous + 1)
      onChanged?.()
    } catch (requestError) {
      setRemoveError(requestError.response?.data?.message || 'Pasagjeri nuk u largua. Provoni përsëri.')
      if ([403, 404, 409].includes(requestError.response?.status)) {
        setReload((previous) => previous + 1)
        onChanged?.()
      }
    } finally {
      removing.current = false
      setRemovingId(null)
    }
  }

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    setReservation(null)
    axios
      .get(`vehicle-reservations/${management ? 'manage/' : ''}${reservationId}`)
      .then(({ data }) => {
        if (!data.success || !data.data) throw new Error('Reservation unavailable')
        if (active) setReservation(data.data)
      })
      .catch((requestError) => {
        if (active) setError(requestError.response?.data?.message || 'Detajet e rezervimit nuk u ngarkuan. Provoni përsëri.')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [reservationId, management, reload])

  return (
    <Modal
      show
      onHide={close}
      backdrop={removingId ? 'static' : true}
      keyboard={!removingId}
      dialogClassName={classes.dialog}
      className='modal-1'
      aria-labelledby='reservation-details-title'
    >
      <Modal.Header closeButton={!removingId}>
        <Modal.Title id='reservation-details-title'>Detajet e rezervimit</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {reservation && (
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            indicatorColor='primary'
            textColor='primary'
            aria-label='Detajet dhe historiku i rezervimit'
          >
            <Tab value='details' label='Detajet' disabled={Boolean(removingId)} />
            <Tab value='history' label='Historiku' disabled={Boolean(removingId)} />
          </Tabs>
        )}
        {tab === 'history' && reservation ? (
          <ReservationHistory reservationId={reservationId} management={management} reloadKey={reload} />
        ) : (
          <>
            {removeError && (
              <Typography role='alert' color='error'>
                {removeError}
              </Typography>
            )}
            {loading && <Typography role='status'>Duke ngarkuar detajet…</Typography>}
            {error && (
              <Box>
                <Typography role='alert' color='error'>
                  {error}
                </Typography>
                <Button onClick={() => setReload((previous) => previous + 1)}>Provo përsëri</Button>
              </Box>
            )}
            {reservation && (
              <>
                <Box component='dl' className={classes.details}>
                  <Detail label='Rezervuesi'>
                    {personName(reservation.owner)}
                    {reservation.owner?.email && (
                      <Typography component='span' display='block' variant='body2' color='textSecondary'>
                        {reservation.owner.email}
                      </Typography>
                    )}
                  </Detail>
                  <Detail label='Statusi'>
                    {{
                      pending: 'Në pritje të miratimit',
                      approved: 'Miratuar',
                      rejected: 'Refuzuar',
                      cancelled: 'Anuluar',
                      completed: 'Përfunduar'
                    }[getReservationStatus(reservation, now)] || reservation.status}
                  </Detail>
                  <Detail label='Data e nisjes'>{moment(reservation.departureDate).format('DD/MM/YYYY')}</Detail>
                  <Detail label='Ora e nisjes'>{reservation.departureTime}</Detail>
                  <Detail label='Data e kthimit'>{moment(reservation.returnDate).format('DD/MM/YYYY')}</Detail>
                  <Detail label='Ora e kthimit'>{reservation.returnTime}</Detail>
                  <Detail label='Destinacioni' className={classes.fullWidth}>
                    {reservation.destination}
                  </Detail>
                  <Detail label='Numri i pasagjerëve'>{reservation.participants.length + 1} (përfshirë rezervuesin)</Detail>
                  <Detail label='Vendet për pasagjerë'>
                    {reservation.vehicles.reduce((total, vehicle) => total + vehicle.passenger_seats, 0)}
                  </Detail>
                  <Detail label='Rast i jashtëzakonshëm'>{reservation.exceptionalCase ? 'Po' : 'Jo'}</Detail>
                  <Detail label='Krijuar më'>
                    {reservation.createdAt ? moment(reservation.createdAt).format('DD/MM/YYYY HH:mm') : '—'}
                  </Detail>
                  {reservation.cancelledAt && (
                    <Detail label='Anuluar më'>{moment(reservation.cancelledAt).format('DD/MM/YYYY HH:mm')}</Detail>
                  )}
                  {reservation.editedAt && (
                    <>
                      <Detail label='Modifikuar nga'>{personName(reservation.editedBy)}</Detail>
                      <Detail label='Modifikuar më'>{moment(reservation.editedAt).format('DD/MM/YYYY HH:mm')}</Detail>
                    </>
                  )}
                  {reservation.reviewDecision && (
                    <>
                      <Detail label='Vendimi'>{{ approved: 'Miratuar', rejected: 'Refuzuar' }[reservation.reviewDecision]}</Detail>
                      <Detail label='Shqyrtuar nga'>{personName(reservation.reviewedBy)}</Detail>
                      <Detail label='Shqyrtuar më'>
                        {reservation.reviewedAt ? moment(reservation.reviewedAt).format('DD/MM/YYYY HH:mm') : '—'}
                      </Detail>
                      {reservation.reviewNote && (
                        <Detail label='Shënimi i vendimit' className={classes.fullWidth}>
                          <span className={classes.reason}>{reservation.reviewNote}</span>
                        </Detail>
                      )}
                    </>
                  )}
                  {reservation.exceptionalCase && (
                    <Detail label='Arsyeja e rastit të jashtëzakonshëm' className={classes.fullWidth}>
                      <span className={classes.reason}>{reservation.exceptionReason}</span>
                    </Detail>
                  )}
                </Box>
                <Box className={classes.section}>
                  <Typography variant='subtitle1' component='h3' gutterBottom>
                    Automjetet
                  </Typography>
                  <TableContainer>
                    <Table size='small' aria-label='Automjetet e rezervimit'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Vehicle ID</TableCell>
                          <TableCell>Numri i targës</TableCell>
                          <TableCell>Marka</TableCell>
                          <TableCell>Modeli</TableCell>
                          <TableCell>Vendet për pasagjerë</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reservation.vehicles.map((vehicle) => (
                          <TableRow key={vehicle.vehicle}>
                            <TableCell>{vehicle.vehicle_id || '—'}</TableCell>
                            <TableCell className={classes.plate}>{vehicle.license_plate}</TableCell>
                            <TableCell>{vehicle.make}</TableCell>
                            <TableCell>{vehicle.model}</TableCell>
                            <TableCell>{vehicle.passenger_seats}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box className={classes.section}>
                  <Typography variant='subtitle1' component='h3' gutterBottom>
                    Punonjësit pjesëmarrës
                  </Typography>
                  {reservation.participants.length > 0 ? (
                    <ul className={classes.participants}>
                      {reservation.participants.map((person, index) => (
                        <li key={person?._id || index} className={classes.participant}>
                          <div>
                            {personName(person)}
                            {person?.email && (
                              <Typography component='span' display='block' variant='body2' color='textSecondary'>
                                {person.email}
                              </Typography>
                            )}
                          </div>
                          {reservation.canRemoveParticipants && !hasDeparted(reservation, now) && person?._id && (
                            <Button
                              variant='outlined'
                              size='small'
                              className={classes.remove}
                              disabled={Boolean(removingId)}
                              aria-label={`Largo pasagjerin ${personName(person)}`}
                              onClick={() => removeParticipant(person)}
                            >
                              {removingId === person._id ? 'Duke larguar…' : 'Largo'}
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography variant='body2' color='textSecondary'>
                      Nuk ka punonjës të tjerë pjesëmarrës.
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {management && reservation?.status === 'pending' && onEdit && (
          <Button variant='outlined' color='primary' disabled={Boolean(removingId)} onClick={() => onEdit(reservationId)}>
            Modifiko
          </Button>
        )}
        <Button disabled={Boolean(removingId)} onClick={close}>
          Mbyll
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ReservationDetailsModal
